import { useEffect, useRef, useState } from 'react';

import { getLatestPostSaleOffer, getPaymentsStatuses } from 'PrivateAPI';

import { captureExceptionWithTeam } from 'Utilities/captureException/captureExceptionWithTeam';
import { TEAM } from 'Utilities/constants';
import { getMWVsSellerDataOld } from 'Utilities/hooks/useLatestPayments.helper';
import useUrlVRM from 'Utilities/hooks/useUrlVRM';
import sleep from 'Utilities/sleep';

import { APIResponse, APIResponseStatus } from 'Services/api.types';

import {
  LatestPostSaleOffer,
  LONG_POOLING_DELAY_TIME,
  LONG_POOLING_EXPIRED_TIME,
  LONG_POOLING_MAX_RETRIES,
  LongPoolingStatus,
  PaymentData,
  PaymentStatus,
  PaymentStatuses,
  PostSaleOfferState,
} from '../Payments.types';

interface UsePaymentLongPoolingProps {
  // @ts-ignore -- for test only
  onAfterLoop?: any;
}

// eslint-disable-next-line import/prefer-default-export
export const usePaymentLongPooling = (props: UsePaymentLongPoolingProps = {}) => {
  const { valuationRef } = useUrlVRM({ optimisticReturn: true });

  const [result, setResult] = useState<PaymentStatus | 'notApprovedByDealer'| null>(null);
  const [paymentReference, setPaymentReference] = useState<PaymentStatuses['reference'] | null>(null);
  const latestOfferState = useRef<PostSaleOfferState | null>(null);

  const [longPoolingStatus, setLongPoolingStatus] = useState<LongPoolingStatus>('PENDING');

  const onAfterLoopRef = useRef(props.onAfterLoop);
  onAfterLoopRef.current = props.onAfterLoop;

  useEffect(() => {
    const controller: AbortController = new AbortController();
    let terminate = false;
    let errorCount = 0;
    const expiredDate: number = new Date().getTime() + LONG_POOLING_EXPIRED_TIME;

    const getLatestOffer = async (): Promise<APIResponse<LatestPostSaleOffer>> => {
      const res = await getLatestPostSaleOffer(valuationRef, undefined, controller)
        .catch((err) => {
          if (err?.code !== 'ERR_CANCELED') {
            captureExceptionWithTeam(err, TEAM.PAYMENTS_FE);
          }
          return {
            status: APIResponseStatus.ERROR,
          };
        });

      return res;
    };

    const getPayment = async (): Promise<APIResponse<PaymentData[]>> => {
      const res = await getPaymentsStatuses(valuationRef, controller).catch((err) => {
        if (err?.code !== 'ERR_CANCELED') {
          captureExceptionWithTeam(err, TEAM.PAYMENTS_FE);
        }
        return {
          status: APIResponseStatus.ERROR,
        };
      });
      return res;
    };

    const processPayment = async () => {
      while (!terminate) {
        if (errorCount >= LONG_POOLING_MAX_RETRIES) {
          setLongPoolingStatus('MAX_RETRIES');
          break;
        }

        if (latestOfferState.current === 'pending' || !latestOfferState.current) {
          if (document.visibilityState === 'visible') {
            const res = await getLatestOffer();
            if (res.status !== APIResponseStatus.ERROR) {
              latestOfferState.current = res.data.state;
              errorCount = 0;
              setResult(res.data.state === 'pending' ? 'notApprovedByDealer' : null);
            } else {
              errorCount++;
            }
          }
          await sleep(LONG_POOLING_DELAY_TIME);
        } else {
          if (document.visibilityState === 'visible') {
            const res = await getPayment();
            if (res.status !== APIResponseStatus.ERROR) {
              errorCount = 0;
              const sellerRelatedPayment = getMWVsSellerDataOld(res.data);
              setResult(sellerRelatedPayment.paymentStatus);
              setPaymentReference(sellerRelatedPayment.reference);
              setLongPoolingStatus('SUCCESS');
            } else {
              errorCount++;
            }
          }
          await sleep(LONG_POOLING_DELAY_TIME);
        }

        if (['failed', 'errored', 'done'].includes(result || '')) {
          break;
        }

        if (new Date().getTime() > expiredDate) {
          break;
        }
        await sleep(LONG_POOLING_DELAY_TIME);
      }
      onAfterLoopRef.current?.(); // for tests, so see if while loop terminated
    };

    if (valuationRef) {
      processPayment();
    } else {
      captureExceptionWithTeam('[usePaymentLongPooling] Cannot pull payments data, valuationRef not found.', TEAM.PAYMENTS_FE);
    }

    return () => {
      controller?.abort();
      terminate = true;
    };
  }, [result, valuationRef]);

  return {
    longPoolingStatus,
    paymentLongPoolingResult: result,
    paymentReference,
  };
};
